import { ChevronLeft } from 'lucide-react';
import { MarkdownPreview } from '@/lib/markdown';

/**
 * Мобильное превью в правой колонке страницы шаблона: реплика
 * VisitInstructionScreen в корпусе телефона.
 *
 * Экран рендерится в логических пикселях устройства (390pt в ширину,
 * как iPhone) и равномерно ужимается под корпус: каждый px внутри
 * равен 1dp мобильного приложения — размеры шрифтов, отступы и сетка
 * фото совпадают с устройством один в один. Сверху — реплика
 * native-stack хедера («Инструкция», minimal back): на мобиле экран
 * открывается с ним, статус-бар и хедер залиты surface.
 */
const SCREEN_LOGICAL_WIDTH = 390;
/** Экран корпуса: 300px − 2×6px inset = 288px */
const SCREEN_PX_WIDTH = 288;
const SCREEN_PX_HEIGHT = 638; // 650px корпуса − 2×6px inset
const SCALE = SCREEN_PX_WIDTH / SCREEN_LOGICAL_WIDTH; // ≈ 0.7385
/** Высота экрана в логических пикселях (заполняет корпус после scale) */
const SCREEN_LOGICAL_HEIGHT = Math.round(SCREEN_PX_HEIGHT / SCALE); // ≈ 864

export function MobilePreview({
  title,
  instruction,
  photoUrls,
}: {
  title: string;
  instruction: string;
  photoUrls: string[];
}) {
  const hasInstruction = Boolean(instruction.trim());

  return (
    <div className="justify-self-center lg:sticky lg:top-6 lg:justify-self-end">
      {/* Корпус в пропорциях смартфона (9:19.5, портрет) фиксированного
          размера: экран спозиционирован абсолютно, поэтому длинный контент
          не растягивает корпус — только скролл внутри экрана */}
      <div className="phone-shadow relative aspect-[9/19.5] w-[300px] max-w-full rounded-[2.5rem] border border-border bg-surface-secondary">
        <div className="pointer-events-none absolute top-3 left-1/2 z-10 h-[16px] w-[70px] -translate-x-1/2 rounded-full bg-black" />
        <div className="absolute inset-[6px] overflow-hidden rounded-[2.1rem]">
          {/* Логический экран устройства, ужатый под корпус. Скроллбар
          скрыт — как на устройстве */}
          <div
            className="flex origin-top-left flex-col bg-surface"
            style={{
              width: SCREEN_LOGICAL_WIDTH,
              height: SCREEN_LOGICAL_HEIGHT,
              transform: `scale(${SCALE})`,
            }}
          >
            {/* Статус-бар (47) + native-хедер (44): surface-фон и hairline,
                как headerStyle/headerShadowVisible на мобиле; minimal back —
                только шеврон, титул bodyMd 16/16 regular */}
            <div className="shrink-0 bg-surface pt-[47px] shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
              <div className="flex h-11 items-center gap-2 px-4">
                <ChevronLeft className="size-5 text-foreground" strokeWidth={2.5} />
                <span className="text-[16px] leading-4 font-normal text-foreground">
                  Инструкция
                </span>
              </div>
            </div>

            {/* Контент: px 16 / pb 24 — как styles.scroll на мобиле.
                min-h-0 — иначе flex-элемент не сжимается ниже высоты
                контента и длинная инструкция не скроллится, а режется */}
            <div className="min-h-0 flex-1 overflow-y-auto bg-background px-4 pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* header: headlineSm 18/24/600 */}
              <h3 className="mt-4 text-[18px] leading-[24px] font-semibold text-foreground">
                {title || 'Шаблон'}
              </h3>

              {/* photoGrid: wrap, gap 8; thumbs 120×120, radius 12 —
                  раскладка как на мобиле при ширине контента 358 */}
              {photoUrls.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {photoUrls.map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt=""
                      className="size-[120px] rounded-[12px] bg-surface-secondary object-cover"
                    />
                  ))}
                </div>
              )}

              {hasInstruction ? (
                <div className="markdown-mobile mt-4">
                  <MarkdownPreview source={instruction} />
                </div>
              ) : (
                photoUrls.length === 0 && (
                  /* Пустое состояние как на мобиле: bodySm 14/20, muted */
                  <p className="mt-4 text-[14px] leading-[20px] text-muted">
                    Инструкция отсутствует
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
